import { Drawing } from '$lib/core/api';
import { vec, type Bounds, type Measurement, type Shape, type Vec2 } from '$lib/core/geom';
import {
  InteractionSession,
  PICK_TOLERANCE_PX,
  TOOLS,
  keyLabel,
  type InteractionState,
  type KeyCommand,
  type KeyInput,
  type Marquee,
  type PointerInput,
  type ToolCursor,
  type ToolId,
  type ToolMode,
} from '$lib/core/interaction';
import {
  UNITS,
  type DrawingDocument,
  type LengthDisplay,
  type LengthFormat,
  type NodeId,
  type Style,
  type Unit,
} from '$lib/core/model';
import { layoutBounds } from '$lib/core/dimensions';
import { pickView, sceneBounds, type ResolvedScene } from '$lib/core/scene';
import {
  ScriptDrawing,
  type ScriptError,
  BASE_LAYER,
  DEFAULT_SETTINGS,
  displayOf,
  fractionProblem,
  layerNameProblem,
  MAX_LAYERS,
  MAX_PARAMS,
  moveLayer,
  nextLayerName,
  nextParamName,
  paramNameProblem,
  precisionProblem,
  viewMarginProblem,
  withBaseLayer,
  type ScriptLayer,
  type ScriptLayers,
  type ScriptParam,
  type ScriptParams,
  type ScriptRuntime,
  type ScriptSettings,
} from '$lib/core/script';
import type { SnapKind, SnapResult, SnapSettings } from '$lib/core/snap';
import {
  DEFAULT_VIEWPORT,
  fitBounds,
  panByScreen,
  resize,
  toWorld,
  zoomAt,
  type Viewport,
} from '$lib/render';
import type { PlaceView } from '$lib/services/place';

/** How long typing must pause before the script runs. */
export const RUN_DELAY_MS = 250;

/**
 * Svelte adapter over the headless drawing and interaction session. It mirrors their state
 * into runes, owns the viewport, and converts screen positions into world space; all
 * editing and interaction rules live in the core.
 */
export class EditorState {
  readonly drawing = new Drawing();
  readonly session = new InteractionSession(this.drawing);
  readonly script = new ScriptDrawing();

  private version = $state(0);
  private scriptVersion = $state(0);
  /**
   * Bumped when the script's text changes other than by typing, such as an undo or Claude's
   * edit, so the code editor shows it.
   */
  replaced = $state(0);
  private runTimer: ReturnType<typeof setTimeout> | null = null;
  private typed = '';
  private interaction = $state.raw<InteractionState>(this.session.state);

  viewport = $state<Viewport>(DEFAULT_VIEWPORT);
  showGrid = $state(true);
  documentId = $state<string | null>(null);
  /** Set from opening a drawing until the canvas is sized and the script has run once. */
  needsFit = $state(false);
  /**
   * The script line to show, a new value each time, so showing the same line again moves;
   * `focus` moves the keys there too, for someone who asked to edit that line.
   */
  sourceLine = $state.raw<{ readonly line: number; readonly focus: boolean } | null>(null);
  /** The script line the code editor's cursor was moved to, whose shapes are highlighted. */
  cursorLine = $state<number | null>(null);
  /** A box the canvas outlines, such as the one a comment picked in the panel is about. */
  shownRegion = $state.raw<Bounds | null>(null);

  /** The shapes the cursor's line drew, from the lines the script recorded as it ran. */
  readonly highlighted: ReadonlySet<NodeId> = $derived.by(() => {
    void this.scriptVersion;
    const line = this.cursorLine;
    return new Set(line === null ? [] : this.script.idsAt(line));
  });

  readonly scene: ResolvedScene = $derived.by(() => {
    this.track();
    return this.drawing.scene;
  });

  readonly document: DrawingDocument = $derived.by(() => {
    this.track();
    return this.drawing.document;
  });

  /** The script's text as last handed to the core, which is what gets saved. */
  readonly source: string = $derived.by(() => {
    void this.scriptVersion;
    return this.script.source;
  });

  /** The drawing's params, in the Parameters panel's order; saved beside the script. */
  readonly params: ScriptParams = $derived.by(() => {
    void this.scriptVersion;
    return this.script.params;
  });

  /** The drawing's layers, layer `0` first, in the Layers panel's order; saved beside the script. */
  readonly layers: ScriptLayers = $derived.by(() => {
    void this.scriptVersion;
    return withBaseLayer(this.script.layers);
  });

  /** The drawing's settings, set in the Document section; saved beside the script. */
  readonly settings: ScriptSettings = $derived.by(() => {
    void this.scriptVersion;
    return this.script.settings;
  });

  /** How panels show lengths: the drawing's display, always with its unit. */
  readonly lengths: LengthDisplay = $derived({ ...displayOf(this.settings), suffix: true });

  readonly scriptError: ScriptError | null = $derived.by(() => {
    void this.scriptVersion;
    return this.script.error;
  });

  readonly activeToolId: ToolId = $derived(this.interaction.toolId);
  readonly toolCursor: ToolCursor = $derived(
    TOOLS.find((tool) => tool.id === this.interaction.toolId)?.cursor ?? 'crosshair',
  );
  readonly toolHint: string = $derived(this.interaction.hint);
  readonly statusMessage: string = $derived(this.interaction.status);
  readonly selection: readonly NodeId[] = $derived(this.interaction.selection);
  readonly hoveredId: NodeId | null = $derived(this.interaction.hoveredId);
  readonly cursor: Vec2 | null = $derived(this.interaction.cursor);
  readonly activeSnap: SnapResult | null = $derived(this.interaction.snap);
  readonly previews: readonly Shape[] = $derived(this.interaction.previews);
  readonly toolMeasurements: readonly Measurement[] = $derived(this.interaction.measurements);
  readonly marquee: Marquee | null = $derived(this.interaction.marquee);
  /** The box the comment tool has marked out, which a new comment is about. */
  readonly commentRegion: Bounds | null = $derived(this.interaction.region);
  readonly placingPoint: boolean = $derived(this.interaction.placingPoint);
  readonly inProgress: boolean = $derived(this.interaction.inProgress);
  readonly snapSettings: SnapSettings = $derived(this.interaction.snapSettings);
  readonly toolModes: readonly ToolMode[] = $derived(this.interaction.toolModes);
  readonly toolMode: string | null = $derived(this.interaction.toolMode);
  readonly panHeld: boolean = $derived(this.interaction.panHeld);

  /** `confirm` asks the person before a change that cannot be read as harmless. */
  constructor(
    private readonly confirm: (message: string, confirmLabel?: string) => Promise<boolean> = () =>
      Promise.resolve(true),
  ) {
    this.drawing.subscribe(() => {
      this.version += 1;
    });
    this.session.subscribe((state) => {
      const before = this.interaction.selection;
      this.interaction = state;
      const [only] = state.selection;
      if (state.selection.length === 1 && only !== undefined && !before.includes(only))
        this.showSource(only);
    });
    this.script.subscribe(() => {
      if (this.script.document !== this.drawing.document) this.drawing.load(this.script.document);
      this.scriptVersion += 1;
      if (this.needsFit && this.script.ran && this.viewport.width > 0) this.zoomToFit();
    });
  }

  attachRuntime(runtime: ScriptRuntime): void {
    this.script.attach(runtime);
  }

  /** Called on every keystroke; the script runs once typing pauses. */
  typeSource(source: string): void {
    this.cancelRun();
    this.typed = source;
    this.runTimer = setTimeout(() => this.runTyped(), RUN_DELAY_MS);
  }

  /** Steps back through the drawing's one history, taking typing not yet run as a step first. */
  undo(): void {
    this.runTyped();
    this.script.undo();
    this.replaced += 1;
  }

  redo(): void {
    this.runTyped();
    this.script.redo();
    this.replaced += 1;
  }

  private runTyped(): void {
    if (!this.runTimer) return;
    this.cancelRun();
    this.script.setSource(this.typed);
  }

  /** Adds a param named `param1`, `param2`, ... with value 0, unless there are too many. */
  addParam(): void {
    const params = this.script.params;
    if (params.length >= MAX_PARAMS) return;
    this.script.setParams([...params, { name: nextParamName(params), value: 0, description: '' }]);
  }

  /** Renames the param at `index`, or says why the name cannot be used. */
  renameParam(index: number, name: string): string | null {
    const problem = paramNameProblem(name, this.script.params, index);
    if (problem === null) this.changeParam(index, { name }, 'name');
    return problem;
  }

  setParamValue(index: number, value: number): void {
    if (Number.isFinite(value)) this.changeParam(index, { value }, 'value');
  }

  describeParam(index: number, description: string): void {
    this.changeParam(index, { description }, 'description');
  }

  removeParam(index: number): void {
    this.script.setParams(this.script.params.filter((_, i) => i !== index));
  }

  /** Each param field changes as it is typed, so quick edits to one field are one undo step. */
  private changeParam(index: number, change: Partial<ScriptParam>, field: string): void {
    const params = this.script.params;
    const param = params[index];
    if (!param) return;
    this.script.setParams(
      params.map((each, i) => (i === index ? { ...param, ...change } : each)),
      `param ${index} ${field}`,
    );
  }

  /** Adds a layer named `layer1`, `layer2`, ... unless there are too many. */
  addLayer(): void {
    const layers = withBaseLayer(this.script.layers);
    if (layers.length >= MAX_LAYERS) return;
    const name = nextLayerName(layers);
    this.script.setLayers([...layers, { name, style: {}, visible: true, locked: false }]);
  }

  /** Renames the layer at `index`, or says why the name cannot be used; layer `0` keeps its name. */
  renameLayer(index: number, name: string): string | null {
    const layers = withBaseLayer(this.script.layers);
    if (layers[index]?.name === BASE_LAYER) return `Layer ${BASE_LAYER} cannot be renamed`;
    const problem = layerNameProblem(name, layers, index);
    if (problem === null) this.changeLayer(index, { name });
    return problem;
  }

  /** Sets the layer's style properties; one set to undefined goes back to the default. */
  styleLayer(index: number, change: { [K in keyof Style]?: Style[K] | undefined }): void {
    const layer = withBaseLayer(this.script.layers)[index];
    if (!layer) return;
    const style: Record<string, unknown> = { ...layer.style, ...change };
    for (const key of Object.keys(style)) if (style[key] === undefined) delete style[key];
    // A color picker changes as it is dragged, so one drag is one undo step.
    const field = Object.keys(change).join() === 'color' ? `layer ${index} color` : undefined;
    this.changeLayer(index, { style: style as Style }, field);
  }

  setLayerVisible(index: number, visible: boolean): void {
    this.changeLayer(index, { visible });
  }

  setLayerLocked(index: number, locked: boolean): void {
    this.changeLayer(index, { locked });
  }

  /** Moves the layer at `from` to `to`, to paint over those before it; layer `0` stays first. */
  moveLayer(from: number, to: number): void {
    const layers = withBaseLayer(this.script.layers);
    const moved = moveLayer(layers, from, to);
    if (moved !== layers) this.script.setLayers(moved);
  }

  /** Removes a layer; layer `0` always stays. */
  removeLayer(index: number): void {
    const layers = withBaseLayer(this.script.layers);
    if (index === 0) return;
    this.script.setLayers(layers.filter((_, i) => i !== index));
  }

  private changeLayer(index: number, change: Partial<ScriptLayer>, field?: string): void {
    const layers = withBaseLayer(this.script.layers);
    const layer = layers[index];
    if (!layer) return;
    this.script.setLayers(
      layers.map((each, i) => (i === index ? { ...layer, ...change } : each)),
      field,
    );
  }

  /** Sets how far each view's frame stands clear of what it holds, or says why it cannot. */
  setViewMargin(margin: number): string | null {
    const problem = viewMarginProblem(margin);
    if (problem === null) this.script.setSettings({ ...this.script.settings, viewMargin: margin });
    return problem;
  }

  /**
   * Changes what the drawing's numbers mean, without changing a number; once anything is
   * drawn, that changes its physical size, so it asks first.
   */
  async setUnit(unit: Unit): Promise<void> {
    const { settings } = this.script;
    if (unit === settings.unit) return;
    const from = UNITS[settings.unit].label.toLowerCase();
    const to = UNITS[unit].label.toLowerCase();
    if (
      this.document.rootIds.length > 0 &&
      !(await this.confirm(
        `Every number in this drawing will be read as ${to} instead of ${from}. No number changes, so the drawing changes physical size.`,
        `Use ${to}`,
      ))
    )
      return;
    this.script.setSettings({ ...this.script.settings, unit });
  }

  setLengthFormat(format: LengthFormat): void {
    this.script.setSettings({ ...this.script.settings, format });
  }

  /** Sets the decimal places decimal formats show, or says why it cannot. */
  setPrecision(precision: number): string | null {
    const problem = precisionProblem(precision);
    if (problem === null) this.script.setSettings({ ...this.script.settings, precision });
    return problem;
  }

  setFraction(fraction: number): void {
    if (fractionProblem(fraction) === null)
      this.script.setSettings({ ...this.script.settings, fraction });
  }

  setDimensionUnits(dimensionUnits: boolean): void {
    this.script.setSettings({ ...this.script.settings, dimensionUnits });
  }

  /** Whether typing has not yet reached the script. */
  get typing(): boolean {
    return this.runTimer !== null;
  }

  /** Shows a version saved elsewhere, such as Claude's edit, keeping the view where it is. */
  applyRemote(
    name: string,
    source: string,
    params: ScriptParams,
    layers: ScriptLayers,
    settings?: ScriptSettings,
  ): void {
    this.cancelRun();
    this.script.replace({ name, source, params, layers, settings: settings ?? DEFAULT_SETTINGS });
    this.replaced += 1;
  }

  private cancelRun(): void {
    if (this.runTimer) clearTimeout(this.runTimer);
    this.runTimer = null;
  }

  /** The key a button shares with the keyboard, written the way this platform writes it. */
  keyLabel(command: KeyCommand): string | null {
    return keyLabel(command);
  }

  /** Subscribes the caller's derivation to every document mutation. */
  private track(): number {
    return this.version;
  }

  pointerDown(screen: Vec2, button: number, shift: boolean): void {
    this.session.pointerDown(this.input(screen, button, shift));
  }

  pointerMove(screen: Vec2, button: number, shift: boolean): void {
    this.session.pointerMove(this.input(screen, button, shift));
  }

  pointerUp(screen: Vec2, button: number, shift: boolean): void {
    this.session.pointerUp(this.input(screen, button, shift));
  }

  pointerLeave(): void {
    this.session.pointerLeave();
  }

  setTool(id: ToolId): void {
    this.session.setTool(id);
  }

  setToolMode(id: string): void {
    this.session.setToolMode(id);
  }

  /** Returns whether the key was claimed, so the caller can suppress the browser default. */
  keyDown(input: KeyInput): { preventDefault: boolean } | null {
    return this.session.keyDown(input);
  }

  keyUp(input: KeyInput): void {
    this.session.keyUp(input);
  }

  contextMenu(): void {
    this.session.contextMenu();
  }

  escape(): void {
    this.session.escape();
  }

  /** Abandons what the active tool holds, such as the comment tool's box once it is posted. */
  cancelTool(): void {
    this.session.cancel();
  }

  select(ids: readonly NodeId[]): void {
    this.session.select(ids);
  }

  toggleSelected(id: NodeId): void {
    this.session.toggleSelected(id);
  }

  /** The script line that made `id`, or null when the script reports none. */
  sourceLineOf(id: NodeId): number | null {
    void this.scriptVersion;
    return this.script.lineOf(id);
  }

  /** Moves the script editor to the line that made `id`, when the script reports one. */
  showSource(id: NodeId, focus = false): void {
    const line = this.script.lineOf(id);
    if (line !== null) this.sourceLine = { line, focus };
  }

  setSnapKind(kind: SnapKind | null): void {
    this.session.setSnapKind(kind);
  }

  /** Makes `kind` the one snap, or turns snapping off if it already was, as its letter does. */
  chooseSnap(kind: SnapKind): void {
    this.session.chooseSnap(kind);
  }

  setOrtho(ortho: boolean): void {
    this.session.setOrtho(ortho);
  }

  setGridSize(gridSize: number): void {
    this.session.setGridSize(gridSize);
  }

  setAngleStep(angleStep: number): void {
    this.session.setAngleStep(angleStep);
  }

  resizeViewport(width: number, height: number): void {
    this.viewport = resize(this.viewport, width, height);
    if (this.needsFit && this.script.ran && width > 0 && height > 0) {
      this.needsFit = false;
      this.zoomToFit();
    }
  }

  panBy(dx: number, dy: number): void {
    this.viewport = panByScreen(this.viewport, dx, dy);
  }

  zoomAtScreen(screen: Vec2, factor: number): void {
    this.viewport = zoomAt(this.viewport, screen, factor);
  }

  zoomToFit(): void {
    this.needsFit = false;
    const bounds = sceneBounds(this.scene);
    this.viewport = bounds
      ? fitBounds(this.viewport, bounds)
      : { ...this.viewport, center: vec(0, 0), zoom: DEFAULT_VIEWPORT.zoom };
  }

  /** Fits the view `id` to the canvas, frame and title included. */
  zoomToView(id: NodeId): void {
    const view = this.scene.views.find((each) => each.id === id);
    if (view) this.viewport = fitBounds(this.viewport, layoutBounds(view.layout));
  }

  /**
   * Outlines `region` and centers the view on it at `zoom`, zooming out only if the box would
   * not fit; null clears the outline. Without a zoom, it fits the box, no closer than a tenth
   * of the drawing's extent, so a box marked by a click still shows what is around it.
   */
  showRegion(region: Bounds | null, zoom?: number): void {
    this.shownRegion = region;
    if (!region) return;
    if (zoom !== undefined) {
      const fit = fitBounds(this.viewport, region);
      this.viewport = { ...fit, zoom: Math.min(zoom, fit.zoom) };
      return;
    }
    const drawn = sceneBounds(this.scene);
    const least = drawn ? Math.max(drawn.max.x - drawn.min.x, drawn.max.y - drawn.min.y) / 10 : 0;
    const grow = (min: number, max: number) => Math.max(0, (least - (max - min)) / 2);
    const dx = grow(region.min.x, region.max.x);
    const dy = grow(region.min.y, region.max.y);
    this.viewport = fitBounds(this.viewport, {
      min: vec(region.min.x - dx, region.min.y - dy),
      max: vec(region.max.x + dx, region.max.y + dy),
    });
  }

  /** A double-click on a view's frame edge fits that view, unless a tool is mid-step. */
  doubleClick(screen: Vec2): void {
    if (this.inProgress) return;
    const world = toWorld(this.viewport, screen);
    const view = pickView(this.scene, world, PICK_TOLERANCE_PX / this.viewport.zoom);
    if (view) this.zoomToView(view.id);
  }

  loadScript(
    name: string,
    source: string,
    documentId: string | null,
    params: ScriptParams = [],
    layers: ScriptLayers = [],
    settings?: ScriptSettings,
    view?: PlaceView,
  ): void {
    this.cancelRun();
    this.documentId = documentId;
    if (view) this.viewport = { ...this.viewport, center: view.center, zoom: view.zoom };
    this.needsFit = !view;
    this.script.load(name, source, params, layers, settings);
    this.session.reset();
    this.shownRegion = null;
  }

  private input(screen: Vec2, button: number, shift: boolean): PointerInput {
    return { world: toWorld(this.viewport, screen), scale: this.viewport.zoom, button, shift };
  }
}
